/****** ExeDialog per DDT2026
 * Sistema wizard basato su URL simile a C2009
 * Usa verifica_valori_ddt_e_salva per determinare il passo successivo
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from "react-router-dom";
import { ProgressSpinner } from 'primereact/progressspinner';
import { useVerificaValoriDdt, usePrintStatus, useBollaByPk } from './queries';
import { StepIndicator } from './steps/StepIndicator';
import { isCouponMode, MODALITA_COUPON_PARAM } from './couponMode';

const STAMPANTE_NESSUNA = '(nessuna stampante)';

const End = ({ verificaData, searchParams, onNavigate }) => {
    const riga = verificaData?.riga_inserita;
    const coupon = verificaData?.coupon_salvato;
    const isCoupon = isCouponMode(searchParams);
    const stampanteUrl = searchParams.get('stampante');
    // Nel coupon la stampa non avviene alla creazione del template ma
    // all'attivazione (emissione del DDT): niente polling dello stato stampa.
    const stampaRichiesta = !isCoupon && stampanteUrl && stampanteUrl !== STAMPANTE_NESSUNA;
    const stamparescontrino = !isCoupon && searchParams.get('stamparescontrino') === 'true';

    // Polling solo se è stata richiesta la stampa e c'è una riga inserita
    const printKey = (stampaRichiesta && riga) ? {
        cod_bolla: riga.cod_bolla,
        num_bolla: riga.num_bolla,
        treg: riga.treg
    } : null;
    const { msgPrint, polling } = usePrintStatus(printKey);

    // Costruisci URL per "Compila DDT simile" con i dati del DDT appena emesso.
    // Esclude targa e conducente (vanno sempre richiesti), pesi, num_bolla, data_bolla
    // e altri valori specifici della singola transazione.
    const buildRipetiUrl = () => {
        const params = new URLSearchParams();
        const carry = [
            'modalita_coupon',
            'cod_bolla',
            'rag_soc',
            'cod_commessa', 'data_inizio', 'cod_artic', 'descrizion',
            'destinazione', 'id_dest', 'destinazione_pattern',
            'cig', 'cup',
            'stampante'
        ];
        carry.forEach(k => {
            const v = searchParams.get(k);
            if (v != null && v !== '') params.set(k, v);
        });
        return `?${params.toString()}`;
    };

    // Senza tara il wizard ripassa da Pesi, dove si sceglie la tara del coupon
    const buildCouponParams = () => {
        const params = new URLSearchParams();
        params.set(MODALITA_COUPON_PARAM, 'true');
        const carry = [
            'cod_bolla',
            'rag_soc',
            'targa',
            'rag_soc_vettore', 'vett_ns',
            'rag_soc_conducente',
            'cod_commessa', 'data_inizio', 'cod_artic', 'descrizion',
            'destinazione', 'id_dest', 'destinazione_pattern',
            'pesospec',
            'num_silos',
            'temperatura',
            'cig', 'cup',
            'stampante', 'stamparescontrino'
        ];
        carry.forEach(k => {
            const v = searchParams.get(k);
            if (v != null && v !== '') params.set(k, v);
        });
        return params;
    };

    if (isCoupon) {
        return (
            <div className="card-body">
                <h5 className="card-title">Coupon salvato</h5>
                {coupon && (
                    <div className="alert alert-success">
                        <strong>Coupon {coupon.cod_bolla} targa {coupon.targa}</strong>
                        {coupon.treg && <> &mdash; {new Date(coupon.treg).toLocaleString('it-IT')}</>}
                    </div>
                )}

                {coupon && (
                    <ul className="list-group mb-3">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                            Cellulare conducente (SMS)
                            <span className="badge bg-secondary">{coupon.cellulare || 'non indicato'}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                            Correzione tara (dtara)
                            <span className="badge bg-secondary">{coupon.dtara ?? 0}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                            Correzione lordo (dlordo)
                            <span className="badge bg-secondary">{coupon.dlordo ?? 0}</span>
                        </li>
                    </ul>
                )}

                <div className="d-flex gap-2">
                    <Link to={buildRipetiUrl()} className="btn btn-primary">
                        Compila coupon simile
                    </Link>
                    <Link to="?" className="btn btn-primary">
                        Nuovo coupon
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="card-body">
            <h5 className="card-title">DDT Completato</h5>
            {riga && (
                <div className="alert alert-success">
                    <strong>DDT inserito:</strong> {riga.cod_bolla} n.&nbsp;{riga.num_bolla} &mdash; {new Date(riga.data).toLocaleString('it-IT')}
                </div>
            )}

            <ul className="list-group mb-3">
                {stampaRichiesta && (
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                        Stampa
                        {polling
                            ? <span className="badge bg-warning text-dark">Attendi&hellip; <ProgressSpinner style={{width:'1.2em', height:'1.2em'}} strokeWidth="6" /></span>
                            : <span className={`badge ${msgPrint ? 'bg-info' : 'bg-secondary'}`}>{msgPrint || 'no stampa'}</span>
                        }
                    </li>
                )}
                {stamparescontrino && (
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                        Scontrino
                        <span className="badge bg-secondary">richiesto</span>
                    </li>
                )}
            </ul>

            <div className="d-flex gap-2">
                <Link to={buildRipetiUrl()} className="btn btn-primary">
                    Compila DDT simile
                </Link>
                <Link to="?" className="btn btn-primary">
                    Nuovo DDT
                </Link>
                <button type="button" className="btn btn-outline-primary" onClick={() => onNavigate(buildCouponParams())}>
                    Crea coupon da questo DDT
                </button>
            </div>
        </div>
    );
};

// Costruisce oggetto input per verifica_valori_ddt_e_salva dai parametri URL
function buildInputFromSearchParams(searchParams) {
    const getInt = (key) => {
        const val = searchParams.get(key);
        return val ? parseInt(val, 10) : null;
    };
    const getFloat = (key) => {
        const val = searchParams.get(key);
        return val ? parseFloat(val) : null;
    };
    const getBool = (key) => searchParams.get(key) === 'true';
    const getStr = (key) => searchParams.get(key) || null;

    return {
        // Modalita' coupon: il parametro assente vale coupon (azione di default)
        modalita_coupon: isCouponMode(searchParams),
        cod_bolla: getStr('cod_bolla'),
        num_bolla: getInt('num_bolla'),
        data_bolla: getStr('data_bolla'),
        rag_soc: getStr('rag_soc'),
        targa: getStr('targa'),
        rag_soc_vettore: getStr('rag_soc_vettore'),
        vett_ns: getBool('vett_ns'),
        rag_soc_conducente: getStr('rag_soc_conducente'),
        cod_commessa: getStr('cod_commessa'),
        data_inizio: getStr('data_inizio'),
        cod_artic: getInt('cod_artic'),
        destinazione: getStr('destinazione'),
        id_dest: getInt('id_dest'),
        destinazione_pattern: getStr('destinazione_pattern'),
        lordo: getFloat('lordo'),
        tara: getFloat('tara'),
        pesospec: getFloat('pesospec'),
        num_silos: getInt('num_silos'),
        temperatura: getInt('temperatura'),
        cig: getStr('cig'),
        cup: getStr('cup'),
        stampante: getStr('stampante'),
        stamparescontrino: getStr('stamparescontrino'),
        cellulare: getStr('cellulare'),
        dtara: getFloat('dtara') ?? 0,
        dlordo: getFloat('dlordo') ?? 0,
        conferma: getBool('conferma'),
    };
}

// Ordine dei parametri URL di input (solo quelli forniti dall'utente, non i derivati dal server)
const URL_PARAMS_ORDER = [
    MODALITA_COUPON_PARAM,
    'cod_bolla', 'num_bolla', 'data_bolla',
    'rag_soc',
    'targa',
    'rag_soc_vettore', 'vett_ns',
    'rag_soc_conducente',
    'cod_commessa', 'data_inizio', 'cod_artic', 'descrizion',
    'destinazione', 'id_dest', 'destinazione_pattern',
    'pesospec',
    'num_silos',
    'temperatura',
    'cig', 'cup',
    'stampante', 'stamparescontrino',
    'tara', 'dtara', 'dlordo',
    'lordo',
    'cellulare',
    'conferma'
];

// Aggiorna parametri URL aggiungendo i nuovi valori senza eliminare quelli esistenti.
// Un valore esplicitamente null elimina il parametro: serve ai campi che l'utente
// puo' svuotare e a quelli che una modalita' di lavoro rende non pertinenti
// (num_bolla e data_bolla in modalita' coupon, lordo nel coupon).
// La cancellazione dei parametri successivi a un valore modificato avviene solo
// quando l'utente modifica l'URL manualmente nel browser (gestita in useEffect).
function updateQueryVariables(currentSearchParams, newQueryVariablesObj) {
    const updatedParams = new URLSearchParams(currentSearchParams);

    // Aggiungi/aggiorna i nuovi valori (solo quelli presenti in URL_PARAMS_ORDER)
    Object.entries(newQueryVariablesObj).forEach(([key, value]) => {
        if (!URL_PARAMS_ORDER.includes(key)) return;
        if (value === null) {
            updatedParams.delete(key);
        } else if (value !== undefined && value !== '') {
            updatedParams.set(key, String(value));
        }
    });

    return updatedParams;
}

// Trova il componente corrispondente al campo_da_verificare
// Supporta componenti con handledFields[] per gestire più campi in una sola pagina
function findComponentByField(campo, componentList) {
    return componentList.find(c => 
        c.displayName === campo || (c.handledFields && c.handledFields.includes(campo))
    );
}

// Rimuove parametri non previsti dall'URL (preserva ripeti_* per "ripeti bolla simile")
function cleanUnexpectedParams(searchParams) {
    const RIPETI_PARAMS = ['ripeti_anno', 'ripeti_num_bolla', 'ripeti_cod_bolla'];
    const cleanedParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
        if (URL_PARAMS_ORDER.includes(key) || RIPETI_PARAMS.includes(key)) {
            cleanedParams.set(key, value);
        }
    }
    return cleanedParams;
}

export function ExeDialog({ reactComponentList }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const { verificaDdt, loading, error } = useVerificaValoriDdt();
    const [verificaData, setVerificaData] = useState(null);
    const lastInput = useRef(null);
    // Tiene traccia dell'ultimo URL impostato dall'applicazione
    // per distinguere modifiche manuali dell'utente nel browser
    const lastAppParams = useRef(null);

    // Parametri per "ripeti bolla simile" (carica dati da bolla esistente)
    const ripetiAnno = searchParams.get('ripeti_anno');
    const ripetiNumBolla = searchParams.get('ripeti_num_bolla');
    const ripetiCodBolla = searchParams.get('ripeti_cod_bolla');
    const { bollaParams, loading: bollaLoading } = useBollaByPk(ripetiAnno, ripetiCodBolla, ripetiNumBolla);

    // Quando arrivano i dati della bolla da ripetere, setta i parametri URL e rimuovi i ripeti_*
    useEffect(() => {
        if (bollaParams && ripetiAnno) {
            const newParams = new URLSearchParams();
            // La modalita' non arriva dalla bolla ripetuta: si conserva quella corrente
            newParams.set(MODALITA_COUPON_PARAM, String(isCouponMode(searchParams)));
            Object.entries(bollaParams).forEach(([k, v]) => newParams.set(k, v));
            lastAppParams.current = newParams;
            setSearchParams(newParams, { replace: true });
        }
    }, [bollaParams, ripetiAnno, searchParams, setSearchParams]);

    // Rimuove parametri non previsti dall'URL all'avvio e quando cambiano.
    // Se l'utente ha modificato manualmente l'URL nel browser (cambiando un valore
    // già presente), elimina i parametri successivi al punto di modifica.
    useEffect(() => {
        let params = cleanUnexpectedParams(searchParams);

        // Rileva modifiche manuali: confronta con l'ultimo URL impostato dall'app
        if (lastAppParams.current) {
            let modificationIndex = null;
            for (const key of URL_PARAMS_ORDER) {
                const prev = lastAppParams.current.get(key);
                const curr = params.get(key);
                // Un parametro che esisteva prima è stato cambiato dall'utente
                if (prev !== null && curr !== null && prev !== curr) {
                    const idx = URL_PARAMS_ORDER.indexOf(key);
                    if (modificationIndex === null || idx < modificationIndex) {
                        modificationIndex = idx;
                    }
                }
            }
            if (modificationIndex !== null) {
                // Rimuovi tutti i parametri successivi al punto di modifica
                URL_PARAMS_ORDER.slice(modificationIndex + 1).forEach(p => {
                    params.delete(p);
                });
            }
        }

        if (params.toString() !== searchParams.toString()) {
            lastAppParams.current = params;
            setSearchParams(params, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Chiama verifica_valori_ddt_e_salva quando cambiano i parametri URL
    // Salta se stiamo caricando una bolla da ripetere (ripeti_* presenti)
    useEffect(() => {
        if (ripetiAnno) return; // attendi che il caricamento bolla completi e setti i params

        const input = buildInputFromSearchParams(searchParams);
        const inputStr = JSON.stringify(input);
        
        // Evita chiamate duplicate
        if (lastInput.current === inputStr) return;
        lastInput.current = inputStr;

        verificaDdt(input).then(data => {
            setVerificaData(data);
        }).catch(err => {
            console.error('Errore verifica:', err);
        });
    }, [searchParams, verificaDdt, ripetiAnno]);

    // Callback per quando un componente completa
    const changeLocation = (MissingComponent, newQueryVariables) => {
        const updatedParams = updateQueryVariables(searchParams, newQueryVariables);
        lastAppParams.current = updatedParams;
        setSearchParams(updatedParams);
    };

    // Callback per aggiornare i parametri URL senza completare il passo.
    // Serve ai campi che riconfigurano il wizard mentre l'utente e' ancora
    // sulla pagina (la modalita' coupon/DDT): il valore deve essere subito
    // visibile a chi legge l'URL (titolo, verifica lato server) ma non deve
    // riempire la cronologia del browser a ogni cambio.
    const updateParams = (newQueryVariables) => {
        const updatedParams = updateQueryVariables(searchParams, newQueryVariables);
        lastAppParams.current = updatedParams;
        setSearchParams(updatedParams, { replace: true });
    };

    // Callback per navigare a uno step precedente
    const navigateToStep = (newParams) => {
        lastAppParams.current = newParams;
        setSearchParams(newParams);
    };

    // Loading bolla da ripetere o loading iniziale
    if (bollaLoading || (loading && !verificaData)) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    // Errore
    if (error) {
        return (
            <div className="card-body">
                <div className="alert alert-danger">
                    <strong>Errore:</strong> {error.message}
                </div>
            </div>
        );
    }

    // Verifica completata
    if (verificaData?.verifica_completata) {
        return <End verificaData={verificaData} searchParams={searchParams} onNavigate={navigateToStep} />;
    }

    // Trova il componente da mostrare
    const campoCorrente = verificaData?.campo_da_verificare;
    const MissingComponent = campoCorrente ? findComponentByField(campoCorrente, reactComponentList) : null;

    if (!MissingComponent) {
        // Se non c'è ancora risposta, mostra loading
        if (!verificaData) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <ProgressSpinner />
                </div>
            );
        }
        // Componente non trovato
        return (
            <div className="card-body">
                <div className="alert alert-warning">
                    Componente non trovato per campo: {campoCorrente}
                </div>
            </div>
        );
    }

    return (
        <>
            <StepIndicator 
                currentStep={campoCorrente} 
                searchParams={searchParams} 
                onNavigate={navigateToStep}
                verificaData={verificaData}
            />
            <MissingComponent 
                currURLSearchParams={searchParams} 
                onFinish={changeLocation.bind(null, MissingComponent)}
                onUpdateParams={updateParams}
                verificaData={verificaData}
                loading={loading}
                verificaDdt={verificaDdt}
                buildInputFromSearchParams={buildInputFromSearchParams}
            />
        </>
    );
}
